import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import Navigation from './components/Navigation';
import SessionDiagnosticPanel from './components/SessionDiagnosticPanel';
import DashboardErrorBoundary from './components/DashboardErrorBoundary';
import LoginPage from './components/auth/LoginPage';
import PasswordPanel from './components/auth/PasswordPanel';
import ProtectedRoute from './components/auth/ProtectedRoute';
import OperationalContextBar from './components/OperationalContextBar';
import { OperationalContextProvider } from './context/OperationalContext';
import {
  ROUTE_IDS,
  findRouteEntry,
  resolveRouteEntry,
  shouldShowOpsContextBar as resolveOpsContextBarFromEntry
} from './config/routeTable';
import { resolveWorkforceRoute, legacyPathForFocus } from './config/workforceRouting';
import { useAuth } from './context/AuthContext';
import { useTenantConfig, useTerminology } from './context/TenantConfigContext';
import { apiRequest } from './services/apiClient';
import { useFetch } from './hooks/useFetch';
import { buildQuery } from './utils/buildQuery';
import OpsManagerDashboard from './pages/OpsManagerDashboard';
import AirportManagerDashboard from './pages/AirportManagerDashboard';
import DirectorDashboard from './pages/DirectorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import AlertsCenter from './pages/AlertsCenter';
import NotificationsInbox from './pages/NotificationsInbox';
import TodayPage from './pages/TodayPage';
import GoalPlanPage from './pages/GoalPlanPage';
import ManagerTimecardsPage from './pages/ManagerTimecardsPage';
import AttendancePage from './pages/AttendancePage';
import TimekeepingReportsPage from './pages/TimekeepingReportsPage';
import TimekeepingAdminPage from './pages/TimekeepingAdminPage';
import EmployeeDirectoryPage from './pages/employees/EmployeeDirectoryPage';
import EmployeeProfilePage from './pages/employees/EmployeeProfilePage';
import SetupWizard from './pages/SetupWizard';
import DashboardPreview from './pages/DashboardPreview';
import LocationSetupPage from './pages/LocationSetupPage';
import StarterMode from './pages/StarterMode';
import UnauthorizedPage from './pages/UnauthorizedPage';
import ModuleNotEnabledPage from './pages/ModuleNotEnabledPage';
import { KPI_CONFIG } from './utils/calculations';
import { createKpiOnlyManagerRecord } from './data/managerKpiData';
import { deriveDateRangeForPeriod } from './utils/periodRange';
import {
  readUrlState,
  writeUrlState,
  buildPathWithState,
  VALID_PERIODS,
  URL_KEY_LOCATION,
  URL_KEY_MANAGER,
  URL_KEY_PERIOD
} from './utils/urlState';
import {
  APP_ROUTE_META,
  canAccessRoute,
  DIRECTOR_SHELL_ROLES,
  getScopeRollupLabel,
  getUserDisplayName,
  getUserReferenceTitle,
  getWorkspaceForUser,
  isKnownWorkspaceUser,
  resolveRouteModuleId,
  STARTER_MODE_NAVIGATION,
  userHasScopeRollupOption
} from './config/appRoutes';
import { formatDocumentTitle, PRODUCT_NAME } from './config/appBranding';
import {
  DIRECTOR_ADMIN_TOAST_EVENT,
  DIRECTOR_DATA_UPDATED_EVENT,
  DIRECTOR_LOCATIONS_UPDATED_EVENT
} from './utils/directorEvents';
import {
  applyFocusQueueCountDelta,
  subscribeToFocusQueueChanges
} from './utils/focusQueueEvents';
import { isPlatformWorkspaceUser } from './utils/platformWorkspace';

const DIRECTOR_LOCATION_TOTAL_ID_PREFIX = '__director_location_total__';
const ALL_DIRECTOR_LOCATIONS = 'ALL';
const DIRECTOR_LOCATION_STORAGE_KEY = 'director-selected-location-code';
const SELECTED_PERIOD_STORAGE_KEY = 'selected-period';
const DEFAULT_SELECTED_PERIOD = 'monthly';
const DEFAULT_APP_TOAST = { message: '', type: 'success', action: null };
const OPEN_VACATION_STATUSES = new Set(['pending', 'review needed', 'blocked by coverage', 'blocked by blackout']);
// FOCUS_QUEUE_ROLES moved to src/config/routeTable.js — the route table
// owns the shell-by-role gate for /today now.

function buildDirectorLocationTotalId(locationCode = ALL_DIRECTOR_LOCATIONS) {
  return `${DIRECTOR_LOCATION_TOTAL_ID_PREFIX}:${locationCode}`;
}

function isGlobalDirectorManager(manager) {
  return String(manager?.managerRoleKey || '').toLowerCase() === 'director';
}

function isVacationEvent(event) {
  return String(event?.eventType || '').toLowerCase().startsWith('vacation');
}

function isOpenVacationRequest(event) {
  return isVacationEvent(event) && OPEN_VACATION_STATUSES.has(String(event?.status || '').toLowerCase());
}

function calculateEventDays(startDate, endDate, timeBlock) {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return 0;
  }

  const dayCount = Math.floor((end - start) / 86400000) + 1;
  const normalizedTimeBlock = String(timeBlock || '').toLowerCase();
  if (dayCount === 1 && ['am', 'pm', 'morning', 'afternoon', 'half day', 'half_day'].includes(normalizedTimeBlock)) {
    return 0.5;
  }

  return dayCount;
}

function buildApprovedVacationEntriesByManager(events) {
  const now = new Date();

  return events.reduce((accumulator, event) => {
    if (!isVacationEvent(event) || String(event.status || '').toLowerCase() !== 'approved') {
      return accumulator;
    }

    const managerId = Number(event.managerId);
    if (!accumulator[managerId]) {
      accumulator[managerId] = [];
    }

    const end = new Date(`${event.endDate}T23:59:59`);
    accumulator[managerId].push({
      id: `vacation-${event.id}`,
      startDate: event.startDate,
      endDate: event.endDate,
      days: calculateEventDays(event.startDate, event.endDate, event.timeBlock),
      status: end < now ? 'used' : 'scheduled',
      reason: event.reason || '',
      timeBlock: event.timeBlock || 'Full Day'
    });

    return accumulator;
  }, {});
}

function buildDirectorGoalTargets({ goals, period, locationCode }) {
  const scopedGoals = goals.filter(goal => {
    if (!goal?.kpiKey || goal.goalPeriodType !== period) {
      return false;
    }

    if (locationCode === ALL_DIRECTOR_LOCATIONS) {
      return true;
    }

    return goal.locationCode === locationCode;
  });

  if (!scopedGoals.length) {
    return {};
  }

  const grouped = scopedGoals.reduce((accumulator, goal) => {
    if (!accumulator[goal.kpiKey]) {
      accumulator[goal.kpiKey] = [];
    }

    accumulator[goal.kpiKey].push(Number(goal.goalValue) || 0);
    return accumulator;
  }, {});

  return Object.entries(grouped).reduce((targets, [kpiKey, values]) => {
    const unit = KPI_CONFIG.find(metric => metric.key === kpiKey)?.unit || '';
    targets[kpiKey] = unit === '$'
      ? values.reduce((sum, value) => sum + value, 0)
      : values.reduce((sum, value) => sum + value, 0) / values.length;
    return targets;
  }, {});
}

function normalizeManagerDashboardRecord(record) {
  if (!record) {
    return null;
  }

  const goalMap = record.goalMap || record.kpiTargets || {};
  const unitMap = record.unitMap || record.kpiUnits || {};

  return {
    ...record,
    goalMap,
    unitMap
  };
}

function getCurrentPath() {
  return window.location.pathname || '/';
}

function resolveRouteAlias(path, user) {
  if (path === '/approvals') {
    return '/requests-calendar';
  }

  if (path === '/manager-performance') {
    if (user?.role === 'airport_manager') {
      return '/station-dashboard';
    }
    if (user?.role === 'ops_manager') {
      return '/my-dashboard';
    }
    return '/director-dashboard';
  }

  return path;
}

/**
 * W1: resolve any workforce URL — legacy or canonical — into the same
 * shape App.js needs (person identity + focus). Returning null means the
 * path is not a workforce route at all.
 */
function getPersonWorkspaceIdentity(path) {
  const resolved = resolveWorkforceRoute(path);
  return {
    employeeProfileId: resolved?.employeeProfileId ?? null,
    managerId: resolved?.managerId ?? null,
    personIdentityType: resolved?.personIdentityType ?? null
  };
}

function getStoredPeriod() {
  try {
    const stored = window.localStorage.getItem(SELECTED_PERIOD_STORAGE_KEY);
    return stored && VALID_PERIODS.includes(stored) ? stored : '';
  } catch {
    return '';
  }
}

// Priority order: URL params override everything, localStorage is the
// fallback, default values are last. Called synchronously in useState
// initializers so there's no post-mount flicker.
function resolveInitialPeriod() {
  if (typeof window === 'undefined') return DEFAULT_SELECTED_PERIOD;
  const fromUrl = readUrlState()[URL_KEY_PERIOD];
  if (fromUrl) return fromUrl;
  const fromStorage = getStoredPeriod();
  if (fromStorage) return fromStorage;
  return DEFAULT_SELECTED_PERIOD;
}

function resolveInitialLocationCode() {
  if (typeof window === 'undefined') return ALL_DIRECTOR_LOCATIONS;
  const fromUrl = readUrlState()[URL_KEY_LOCATION];
  if (fromUrl) return fromUrl;
  return getStoredDirectorLocationCode();
}

function getStoredDirectorLocationCode() {
  try {
    return window.localStorage.getItem(DIRECTOR_LOCATION_STORAGE_KEY) || ALL_DIRECTOR_LOCATIONS;
  } catch (error) {
    return ALL_DIRECTOR_LOCATIONS;
  }
}

function getAssignedLocationCodes(user) {
  const codes = [
    ...(Array.isArray(user?.assignedLocationCodes) ? user.assignedLocationCodes : []),
    user?.locationCode
  ]
    .map(code => String(code || '').trim().toUpperCase())
    .filter(Boolean);

  return [...new Set(codes)];
}

function buildStationSummary({ dashboardData, managers, requests, user, locationCode, locationLabel, stationCountOverride, attendanceSummary }) {
  const managerRecords = managers
    .map(manager => dashboardData.managers[manager.id])
    .filter(Boolean);
  const atWorkRate = Number(attendanceSummary?.atWorkRate || 0);

  return {
    atWorkRate,
    kpiCount: managerRecords[0] ? Object.keys(managerRecords[0].kpis?.weekly || {}).length : 0,
    locationCode: locationCode || user?.locationCode || managerRecords[0]?.locationCode || 'N/A',
    locationName: locationLabel || user?.locationName || 'Assigned station',
    managerCount: managerRecords.length,
    opsManagerCount: managers.filter(manager => !String(manager.roleTitle || '').toLowerCase().includes('airport manager')).length,
    pendingRequests: requests.filter(isOpenVacationRequest).length,
    stationCount: typeof stationCountOverride === 'number' ? stationCountOverride : new Set(managers.map(manager => manager.locationCode)).size
  };
}

function buildHeaderSummary({ currentWorkspace, user, stationSummary, selectedLocationCode, selectedLocationLabel }) {
  const stationCount = Math.max(Number(stationSummary?.stationCount || 0), 0);
  const pluralizedStations = `${stationCount} Location${stationCount === 1 ? '' : 's'}`;
  const allLocationsLabel = getScopeRollupLabel(user) || 'Company Rollup';

  if (currentWorkspace === 'admin') {
    return {
      label: 'Governance Workspace',
      value: selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allLocationsLabel : selectedLocationLabel,
      subtitle: stationCount > 0 ? pluralizedStations : 'Platform Governance'
    };
  }

  if (currentWorkspace === 'director') {
    return {
      label: 'Command Center',
      value: selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allLocationsLabel : selectedLocationLabel,
      subtitle: stationCount > 0 ? pluralizedStations : 'Leadership Command Center'
    };
  }

  const assignedStation = user?.locationName || user?.locationCode || 'Assigned Location';
  const stationCode = user?.locationCode ? `Location ${user.locationCode}` : 'Assigned location';

  if (currentWorkspace === 'airport') {
    return {
      label: 'Location Dashboard',
      value: assignedStation,
      subtitle: stationCode
    };
  }

  return {
    label: 'My Dashboard',
    value: assignedStation,
    subtitle: stationCode
  };
}

function buildScopeLabel({ isDirectorShellUser, selectedLocation, selectedLocationCode, stationSummary, allDirectorLocationsLabel, user }) {
  if (isDirectorShellUser) {
    if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
      return allDirectorLocationsLabel;
    }

    if (!selectedLocationCode) {
      return 'Assigned locations';
    }

    return selectedLocation?.name
      ? `${selectedLocationCode} - ${selectedLocation.name}`
      : selectedLocationCode;
  }

  if (stationSummary?.locationCode && stationSummary?.locationName) {
    return `${stationSummary.locationCode} - ${stationSummary.locationName}`;
  }

  return stationSummary?.locationCode || user?.locationCode || 'Assigned location';
}

function aggregateSeries(entries, unit) {
  const keys = new Set();
  entries.forEach(entry => Object.keys(entry || {}).forEach(key => keys.add(key)));

  return Array.from(keys).sort((a, b) => Number(a) - Number(b)).reduce((accumulator, key) => {
    const values = entries
      .map(entry => Number(entry?.[key]))
      .filter(value => Number.isFinite(value));

    accumulator[key] = values.length
      ? (unit === '$'
        ? values.reduce((sum, value) => sum + value, 0)
        : values.reduce((sum, value) => sum + value, 0) / values.length)
      : 0;
    return accumulator;
  }, {});
}

function buildDirectorLocationTotalRecord({ dashboardData, locationCode, locationLabel, managers, stationSummary }) {
  const managerRecords = managers
    .map(manager => normalizeManagerDashboardRecord(dashboardData.managers[manager.id]))
    .filter(Boolean);

  const unitMap = {};
  const kpis = ['weekly', 'monthly', 'quarterly', 'yearly'].reduce((periods, period) => {
    periods[period] = {};

    KPI_CONFIG.forEach(metric => {
      unitMap[metric.key] = metric.unit;

      const metricSeries = managerRecords.map(record => record.kpis?.[period]?.[metric.key] || {});
      periods[period][metric.key] = aggregateSeries(metricSeries, metric.unit);
    });

    return periods;
  }, {});

  return {
    id: buildDirectorLocationTotalId(locationCode || ALL_DIRECTOR_LOCATIONS),
    locationCode: stationSummary.locationCode || locationCode || ALL_DIRECTOR_LOCATIONS,
    name: locationCode && locationCode !== ALL_DIRECTOR_LOCATIONS ? `${locationCode} Total` : 'Location Total',
    role: 'Director Overview',
    position: locationLabel || 'Location Total',
    department: locationCode && locationCode !== ALL_DIRECTOR_LOCATIONS ? `${locationLabel} Dashboard` : 'All Active Locations',
    notes: `Rolled up from ${managers.length} active managers across ${stationSummary.stationCount} locations.`,
    goalMap: {},
    unitMap,
    kpis,
    attendance: managerRecords.flatMap(record => record.attendance || []),
    vacations: managerRecords.flatMap(record => record.vacations || [])
  };
}

function buildEmptyManagerDashboardRecord(manager) {
  if (!manager) {
    return null;
  }

  return {
    id: manager.id,
    locationCode: manager.locationCode,
    name: manager.name,
    role: manager.roleTitle || 'Manager',
    position: manager.roleTitle || 'Manager',
    department: manager.locationName || manager.locationCode || 'Dashboard',
    notes: `Live dashboard fallback for ${manager.name}.`,
    goalMap: {},
    unitMap: {},
    kpis: {
      weekly: {},
      monthly: {},
      quarterly: {},
      yearly: {}
    },
    attendance: [],
    vacations: []
  };
}

function App() {
  const { authError, changePassword, enterClientPortal, exitClientPortal, isAuthenticated, isLoading, login, logout, refreshCurrentUser, resetUserPassword, user } = useAuth();
  const { getModuleMetadata, isModuleEnabled } = useTenantConfig();
  const {
    companyDisplayName,
    formatRoleLabel: formatTenantRoleLabel,
    formatWorkspaceLabel: formatTenantWorkspaceLabel,
    getNavigationForRole: getTenantNavigationForRole
  } = useTerminology();
  const [selectedPeriod, setSelectedPeriod] = useState(resolveInitialPeriod);
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedLocationCode, setSelectedLocationCode] = useState(resolveInitialLocationCode);
  const hasResolvedDefaultScopeRef = React.useRef(false);
  const [accessibleManagers, setAccessibleManagers] = useState([]);
  const [accessibleLocations, setAccessibleLocations] = useState([]);
  const [accessibleGoals, setAccessibleGoals] = useState([]);
  const [accessibleEvents, setAccessibleEvents] = useState([]);
  const [managedUsers, setManagedUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [attendanceScopeSummary, setAttendanceScopeSummary] = useState(null);
  const [screenError, setScreenError] = useState('');
  const [appToast, setAppToast] = useState(DEFAULT_APP_TOAST);
  const [isPasswordPanelOpen, setIsPasswordPanelOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(getCurrentPath);
  const resolvedCurrentPath = resolveRouteAlias(currentPath, user);
  const [pendingCounts, setPendingCounts] = useState({ pendingForReview: 0, returnedToMe: 0, focusQueueTotal: 0 });
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [runtimeVersion, setRuntimeVersion] = useState(null);
  // Production-triage panel. Activated via ?diag=1 on any in-app URL.
  // Calls /api/auth/session-diagnostics + /api/platform/diagnostics
  // through apiRequest (credentials:include) so it always uses the
  // same auth path as every other authenticated call. Sidesteps the
  // "direct browser nav doesn't send the cookie" failure mode.
  const [isDiagnosticOpen, setIsDiagnosticOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const qs = new URLSearchParams(window.location?.search || '');
    return qs.get('diag') === '1';
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const routeTitle = APP_ROUTE_META[currentPath]?.title || APP_ROUTE_META[resolvedCurrentPath]?.title || null;
    document.title = formatDocumentTitle(routeTitle, companyDisplayName || user?.companyName);
  }, [companyDisplayName, currentPath, resolvedCurrentPath, user?.companyName]);
  const isPlatformClientPortal = Boolean(user?.viewingClientPortal && isPlatformWorkspaceUser(user));
  const activeWorkspace = useMemo(
    () => (isPlatformClientPortal ? 'director' : getWorkspaceForUser(user)),
    [isPlatformClientPortal, user]
  );
  const isDirectorShellUser = DIRECTOR_SHELL_ROLES.has(user?.role);
  const isAdminUser = activeWorkspace === 'admin';
  const isStarterMode = Boolean(onboardingStatus?.starterMode && isDirectorShellUser);

  const refreshOnboardingStatus = React.useCallback(async () => {
    if (!isAuthenticated || !user?.companyId) {
      setOnboardingStatus(null);
      return null;
    }

    const nextStatus = await apiRequest('/api/onboarding/status');
    setOnboardingStatus(nextStatus);
    return nextStatus;
  }, [isAuthenticated, user?.companyId]);

  useEffect(() => {
    if (!isAuthenticated || !user?.companyId) {
      setOnboardingStatus(null);
      return undefined;
    }

    let isMounted = true;
    apiRequest('/api/onboarding/status')
      .then(nextStatus => {
        if (isMounted) {
          setOnboardingStatus(nextStatus);
        }
      })
      .catch(() => {
        if (isMounted) {
          setOnboardingStatus(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?.companyId]);

  useEffect(() => {
    if (!isAuthenticated || !isDirectorShellUser) {
      setRuntimeVersion(null);
      return undefined;
    }

    let isMounted = true;
    apiRequest('/api/health')
      .then((health) => {
        if (isMounted) {
          setRuntimeVersion(health);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRuntimeVersion(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isDirectorShellUser]);

  useEffect(() => {
    if (!isAuthenticated || !user?.companyId || !['/setup', '/dashboard-preview'].includes(currentPath)) {
      return;
    }

    refreshOnboardingStatus().catch(() => {});
  }, [accessibleGoals.length, accessibleLocations.length, accessibleManagers.length, currentPath, isAuthenticated, refreshOnboardingStatus, user?.companyId]);

  const loadAuthorizedData = React.useCallback(async () => {
    const [managerRows, policyRows, locationRows, goalRows, eventRows] = await Promise.all([
      apiRequest('/api/managers'),
      apiRequest('/api/policies'),
      apiRequest('/api/locations'),
      apiRequest('/api/goals/location'),
      apiRequest('/api/events')
    ]);
    const activeLocationCodes = new Set(
      locationRows
        .filter(location => location.isActive)
        .map(location => String(location.code || '').toUpperCase())
    );
    const locationNameByCode = locationRows.reduce((accumulator, location) => {
      accumulator[location.code] = location.name;
      return accumulator;
    }, {});
    const isActiveLocationCode = (locationCode) => (
      !locationCode
      || activeLocationCodes.has(String(locationCode || '').toUpperCase())
    );

    return {
      eventRows: eventRows.filter(event => isActiveLocationCode(event.locationCode)),
      goalRows: goalRows.filter(goal => isActiveLocationCode(goal.locationCode)),
      locationRows,
      managerRows: managerRows
        .filter(manager => (
          isActiveLocationCode(manager.locationCode)
          || isGlobalDirectorManager(manager)
        ))
        .map(manager => ({
          id: manager.managerKey,
          managerDbId: manager.id,
          name: `${manager.firstName} ${manager.lastName}`,
          email: manager.email,
          hireDate: manager.hireDate || '',
          locationCode: manager.locationCode,
          locationName: locationNameByCode[manager.locationCode] || manager.locationName || manager.locationCode,
          locationId: manager.locationId,
          roleTitle: manager.roleTitle
        }))
        .filter(manager => Boolean(manager.id)),
      policyRows: policyRows.filter(policy => isActiveLocationCode(policy.locationCode))
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setAccessibleManagers([]);
      setAccessibleLocations([]);
      setAccessibleGoals([]);
      setAccessibleEvents([]);
      setSelectedManager('');
      setSelectedLocationCode(ALL_DIRECTOR_LOCATIONS);
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const { eventRows, goalRows, locationRows, managerRows, policyRows } = await loadAuthorizedData();

        if (!isMounted) {
          return;
        }

        setAccessibleManagers(managerRows);
        setAccessibleLocations(locationRows);
        setAccessibleGoals(goalRows);
        setAccessibleEvents(eventRows);
        setPolicies(policyRows);
        setScreenError('');
      } catch (error) {
        if (isMounted) {
          setScreenError(error.message || 'Unable to load authorized dashboard data.');
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, loadAuthorizedData]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasResolvedDefaultScopeRef.current = false;
      return;
    }
    if (hasResolvedDefaultScopeRef.current) return;
    if (!user || !accessibleLocations.length) return;
    if (user.accessAllLocations) {
      hasResolvedDefaultScopeRef.current = true;
      return;
    }
    const assignedIds = Array.isArray(user.assignedLocationIds) ? user.assignedLocationIds : [];
    if (!assignedIds.length) {
      hasResolvedDefaultScopeRef.current = true;
      return;
    }
    if (selectedLocationCode !== ALL_DIRECTOR_LOCATIONS) {
      hasResolvedDefaultScopeRef.current = true;
      return;
    }
    const urlOverride = typeof window !== 'undefined' && readUrlState()[URL_KEY_LOCATION];
    if (urlOverride) {
      hasResolvedDefaultScopeRef.current = true;
      return;
    }
    const firstAssigned = accessibleLocations.find(location => Number(location.id) === Number(assignedIds[0]));
    if (firstAssigned?.code) {
      setSelectedLocationCode(firstAssigned.code);
    }
    hasResolvedDefaultScopeRef.current = true;
  }, [isAuthenticated, user, accessibleLocations, selectedLocationCode]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPendingCounts({ pendingForReview: 0, returnedToMe: 0, focusQueueTotal: 0 });
      return undefined;
    }

    let isMounted = true;

    const fetchCounts = async () => {
      const [approvalResult, focusQueueResult] = await Promise.allSettled([
        apiRequest('/api/approvals/counts'),
        apiRequest('/api/focus-queue/counts')
      ]);
      if (!isMounted) return;
      const approvalCounts = approvalResult.status === 'fulfilled'
        ? approvalResult.value
        : {};
      const focusQueueCounts = focusQueueResult.status === 'fulfilled'
        ? focusQueueResult.value
        : {};
      setPendingCounts({
        pendingForReview: Number(approvalCounts?.pendingForReview || 0),
        returnedToMe: Number(approvalCounts?.returnedToMe || 0),
        focusQueueTotal: Number(focusQueueCounts?.total || 0)
      });
    };

    fetchCounts();
    const intervalId = setInterval(fetchCounts, 60000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchCounts();
    };
    const handleDirectorDataUpdated = () => fetchCounts();
    const unsubscribeFocusQueue = subscribeToFocusQueueChanges((event) => {
      const { action, item } = event.detail || {};
      if ((action === 'dismiss' || action === 'restore') && item?.itemKey) {
        setPendingCounts(current => {
          const nextCounts = applyFocusQueueCountDelta(
            { total: current.focusQueueTotal, overdue: 0, byType: {} },
            item,
            action === 'dismiss' ? -1 : 1
          );
          return {
            ...current,
            focusQueueTotal: nextCounts.total
          };
        });
        return;
      }
      if (action === 'refresh') {
        fetchCounts();
      }
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      unsubscribeFocusQueue();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isDirectorShellUser) {
      return undefined;
    }

    let isMounted = true;

    const handleDirectorDataRefresh = async () => {
      try {
        const { eventRows, goalRows, locationRows, managerRows, policyRows } = await loadAuthorizedData();
        if (!isMounted) {
          return;
        }

        setAccessibleManagers(managerRows);
        setAccessibleLocations(locationRows);
        setAccessibleGoals(goalRows);
        setAccessibleEvents(eventRows);
        setPolicies(policyRows);
        setScreenError('');
      } catch (error) {
        if (isMounted) {
          setScreenError(error.message || 'Unable to refresh director data.');
        }
      }
    };

    const handleDirectorLocationsUpdatedEvent = async (event) => {
      const nextMessage = event?.detail?.message || 'Location scope refreshed.';
      const nextType = event?.detail?.type || 'success';
      const nextAction = event?.detail?.action || null;
      setAppToast({ message: nextMessage, type: nextType, action: nextAction });
      await handleDirectorDataRefresh();
    };

    const handleDirectorDataUpdatedEvent = async (event) => {
      const nextMessage = event?.detail?.message || 'Director data refreshed.';
      const nextType = event?.detail?.type || 'success';
      const nextAction = event?.detail?.action || null;
      setAppToast({ message: nextMessage, type: nextType, action: nextAction });
      await handleDirectorDataRefresh();
    };

    const handleDirectorAdminToastEvent = (event) => {
      setAppToast({
        message: event?.detail?.message || 'Director portal updated.',
        type: event?.detail?.type || 'success',
        action: event?.detail?.action || null
      });
    };

    window.addEventListener(DIRECTOR_LOCATIONS_UPDATED_EVENT, handleDirectorLocationsUpdatedEvent);
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdatedEvent);
    window.addEventListener(DIRECTOR_ADMIN_TOAST_EVENT, handleDirectorAdminToastEvent);

    return () => {
      isMounted = false;
      window.removeEventListener(DIRECTOR_LOCATIONS_UPDATED_EVENT, handleDirectorLocationsUpdatedEvent);
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdatedEvent);
      window.removeEventListener(DIRECTOR_ADMIN_TOAST_EVENT, handleDirectorAdminToastEvent);
    };
  }, [isAuthenticated, isDirectorShellUser, loadAuthorizedData, user]);

  useEffect(() => {
    if (!appToast.message) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setAppToast(DEFAULT_APP_TOAST);
    }, 2600);

    return () => window.clearTimeout(timeoutId);
  }, [appToast]);

  const handleToastAction = async () => {
    if (typeof appToast.action?.onClick !== 'function') {
      return;
    }

    const action = appToast.action;
    setAppToast(DEFAULT_APP_TOAST);
    await action.onClick();
  };

  const handleEnterClientPortal = async ({ companySlug, companyName, targetPath } = {}) => {
    const nextPath = String(targetPath || '/executive').trim() || '/executive';
    await enterClientPortal({ companySlug });
    window.history.pushState({}, '', nextPath);
    setCurrentPath(nextPath);
    setAppToast({
      message: `Acting as ${companyName || 'client portal'}.`,
      type: 'warning',
      action: null
    });
  };

  const handleExitClientPortal = async () => {
    const clientName = user?.companyName || user?.clientCompanyName || 'client portal';
    await exitClientPortal();
    window.history.pushState({}, '', '/admin-workspace');
    setCurrentPath('/admin-workspace');
    setAppToast({
      message: `Exited ${clientName}. Back in VizionOps.`,
      type: 'success',
      action: null
    });
  };

  useEffect(() => {
    // Browser back/forward should restore both path AND tracked filter
    // state. Missing params fall back to existing state (no accidental
    // reset); invalid params are already filtered by readUrlState.
    const handlePopState = () => {
      setCurrentPath(getCurrentPath());
      const next = readUrlState();
      if (next[URL_KEY_PERIOD]) {
        setSelectedPeriod(next[URL_KEY_PERIOD]);
      }
      if (next[URL_KEY_LOCATION]) {
        setSelectedLocationCode(next[URL_KEY_LOCATION]);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync filter state → URL (replaceState, so filter tweaks never
  // pollute the back-button history). writeUrlState is a no-op when
  // nothing would change, so this effect is safe to run on every
  // sticky-state update.
  useEffect(() => {
    writeUrlState({
      [URL_KEY_LOCATION]: selectedLocationCode && selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
        ? selectedLocationCode
        : null,
      [URL_KEY_PERIOD]: selectedPeriod && selectedPeriod !== DEFAULT_SELECTED_PERIOD
        ? selectedPeriod
        : null
    }, { mode: 'replace' });
  }, [selectedLocationCode, selectedPeriod]);

  // Persist period to localStorage so returning users see their
  // preferred view, mirroring how location is already persisted.
  useEffect(() => {
    try {
      if (selectedPeriod && VALID_PERIODS.includes(selectedPeriod)) {
        window.localStorage.setItem(SELECTED_PERIOD_STORAGE_KEY, selectedPeriod);
      }
    } catch {
      // Keep period in memory if storage is unavailable.
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (user?.mustChangePassword) {
      setIsPasswordPanelOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (!isDirectorShellUser) {
      return;
    }

    try {
      window.localStorage.setItem(DIRECTOR_LOCATION_STORAGE_KEY, selectedLocationCode || ALL_DIRECTOR_LOCATIONS);
    } catch (error) {
      // Keep location scope in memory if storage is unavailable.
    }
  }, [isDirectorShellUser, selectedLocationCode, user]);

  useEffect(() => {
    if (!accessibleManagers.length && !isDirectorShellUser) {
      return;
    }

    const directorScopedManagers = isDirectorShellUser
      ? (selectedLocationCode === ALL_DIRECTOR_LOCATIONS
        ? accessibleManagers
        : accessibleManagers.filter(manager => (
          manager.locationCode === selectedLocationCode
          || isGlobalDirectorManager(manager)
        )))
      : accessibleManagers;
    const directorLocationTotalId = buildDirectorLocationTotalId(selectedLocationCode);
    const scopedNonDirectorLocationCode = !isDirectorShellUser
      ? (user?.locationCode || '')
      : '';
    const scopedNonDirectorScorecardManagerId = scopedNonDirectorLocationCode
      ? buildDirectorLocationTotalId(scopedNonDirectorLocationCode)
      : '';
    const availableManagerIds = isDirectorShellUser
      ? [directorLocationTotalId, ...directorScopedManagers.map(manager => manager.id)]
      : (currentPath === '/location-scorecard' && scopedNonDirectorScorecardManagerId
        ? [scopedNonDirectorScorecardManagerId]
        : accessibleManagers.map(manager => manager.id));
    const preferredManagerId = isDirectorShellUser
      ? directorLocationTotalId
      : (currentPath === '/location-scorecard' && scopedNonDirectorScorecardManagerId
        ? scopedNonDirectorScorecardManagerId
        : (user?.managerKey || accessibleManagers[0]?.id || ''));
    const nextSelectedManager = availableManagerIds.includes(selectedManager)
      ? selectedManager
      : preferredManagerId;

    if (nextSelectedManager !== selectedManager) {
      setSelectedManager(nextSelectedManager);
    }
  }, [accessibleManagers, currentPath, isDirectorShellUser, selectedLocationCode, selectedManager, user]);

  const navGroups = useMemo(() => {
    const navigationRole = isPlatformClientPortal ? 'director' : user?.role;
    const navigation = isStarterMode ? STARTER_MODE_NAVIGATION : getTenantNavigationForRole(navigationRole);
    if (!navigation.length) {
      return [];
    }

    return navigation
      .map(group => ({
        ...group,
        items: (group.items || []).filter(item => canAccessRoute(user, item.path))
      }))
      .filter(group => (group.items || []).length > 0);
  }, [getTenantNavigationForRole, isPlatformClientPortal, isStarterMode, user]);

  const navItems = useMemo(
    () => navGroups.flatMap(group => group.items || []),
    [navGroups]
  );
  const defaultPath = navItems[0]?.path || '/';
  const hiddenAllowedPaths = useMemo(
    () => (isDirectorShellUser ? ['/location-setup'] : []),
    [isDirectorShellUser]
  );
  const personWorkspaceIdentity = getPersonWorkspaceIdentity(currentPath);
  const isParameterizedRegisteredRoute = Boolean(findRouteEntry(currentPath)?.match);

  useEffect(() => {
    if (!isAuthenticated || !defaultPath) {
      return;
    }

    // After the People & Talent nav consolidation, only `/employees` is in
    // navItems for directors, but valid deep-link routes like
    // `/employees/expiring`, `/employees/documents`, `/users`, and
    // `/managers` still need to render their own screens. Whitelisting only
    // navItems caused these routes to bounce back to `/today` (the
    // defaultPath) the moment they were entered — that's exactly the
    // "quick pills route to Today" bug.
    //
    // Allowed paths = anything in the nav + anything registered in
    // APP_ROUTE_META (excluding parameterized routes, which we match via
    // the route table itself) + any hidden-but-allowed director routes.
    const registeredRoutes = Object.keys(APP_ROUTE_META).filter(path => !path.includes(':'));
    const allowedPaths = [
      ...navItems.map(item => item.path),
      ...registeredRoutes,
      ...hiddenAllowedPaths
    ];
    if (!allowedPaths.includes(currentPath) && !isParameterizedRegisteredRoute) {
      window.history.replaceState({}, '', defaultPath);
      setCurrentPath(defaultPath);
    }
  }, [currentPath, defaultPath, hiddenAllowedPaths, isAuthenticated, isParameterizedRegisteredRoute, navItems]);

  const managers = useMemo(() => accessibleManagers.map(manager => ({
    ...manager,
    icon: 'fa-user-tie'
  })), [accessibleManagers]);

  const directorLocationOptions = useMemo(
    () => accessibleLocations
      .filter(location => location.isActive)
      .map(location => ({
        code: location.code,
        name: location.name,
        id: location.id,
        primaryDirectorOwner: location.primaryDirectorOwner || null
      }))
      .sort((a, b) => a.code.localeCompare(b.code)),
    [accessibleLocations]
  );
  const directorVisibleLocationOptions = useMemo(() => {
    if (!isDirectorShellUser || user?.accessAllLocations) {
      return directorLocationOptions;
    }

    const assignedCodes = new Set(getAssignedLocationCodes(user));
    if (!assignedCodes.size) {
      return directorLocationOptions;
    }

    return directorLocationOptions.filter(location => assignedCodes.has(String(location.code || '').toUpperCase()));
  }, [directorLocationOptions, isDirectorShellUser, user]);
  const allDirectorLocationsLabel = getScopeRollupLabel(user) || 'Company Rollup';
  const showRollupOption = userHasScopeRollupOption(user);
  const canUseDirectorRollupScope = isDirectorShellUser && showRollupOption;
  const showDirectorLocationControl = isDirectorShellUser && (
    canUseDirectorRollupScope
    || directorVisibleLocationOptions.length > 1
  );

  const selectedLocation = useMemo(
    () => directorVisibleLocationOptions.find(location => location.code === selectedLocationCode)
      || directorLocationOptions.find(location => location.code === selectedLocationCode)
      || null,
    [directorLocationOptions, directorVisibleLocationOptions, selectedLocationCode]
  );
  const selectedLocationOwnerLabel = selectedLocation?.primaryDirectorOwner?.name || '';
  const selectedLocationOwnerId = selectedLocation?.primaryDirectorOwner?.id || null;

  const approvedVacationEntriesByManager = useMemo(
    () => buildApprovedVacationEntriesByManager(accessibleEvents),
    [accessibleEvents]
  );

  useEffect(() => {
    if (!isDirectorShellUser) {
      return;
    }

    if (directorVisibleLocationOptions.length === 1 && !canUseDirectorRollupScope) {
      const onlyLocationCode = directorVisibleLocationOptions[0].code;
      if (selectedLocationCode !== onlyLocationCode) {
        setSelectedLocationCode(onlyLocationCode);
      }
      return;
    }

    if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
      return;
    }

    const locationStillExists = directorVisibleLocationOptions.some(location => location.code === selectedLocationCode);
    if (!locationStillExists && canUseDirectorRollupScope) {
      setSelectedLocationCode(ALL_DIRECTOR_LOCATIONS);
    }
  }, [canUseDirectorRollupScope, directorVisibleLocationOptions, isDirectorShellUser, selectedLocationCode, user]);

  const directorScopedManagers = useMemo(() => {
    if (!isDirectorShellUser) {
      return managers;
    }

    if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
      return managers;
    }

    return managers.filter(manager => manager.locationCode === selectedLocationCode);
  }, [isDirectorShellUser, managers, selectedLocationCode]);

  const directorSelectableManagers = useMemo(() => {
    if (!isDirectorShellUser) {
      return managers;
    }

    if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
      return managers;
    }

    return managers.filter(manager => (
      manager.locationCode === selectedLocationCode
      || isGlobalDirectorManager(manager)
    ));
  }, [isDirectorShellUser, managers, selectedLocationCode]);

  const stationManagers = useMemo(() => {
    if (!user?.locationCode) {
      return managers;
    }

    return managers.filter(manager => manager.locationCode === user.locationCode);
  }, [managers, user]);

  const visibleDashboardData = useMemo(() => ({
    managers: Object.fromEntries(
      managers
        .map(manager => {
          const fallbackRecord = buildEmptyManagerDashboardRecord(manager);
          const kpiOnlyRecord = createKpiOnlyManagerRecord(manager.id);
          const baseRecord = {
            ...fallbackRecord,
            ...normalizeManagerDashboardRecord(kpiOnlyRecord || {})
          };

          return [manager.id, {
            ...baseRecord,
            id: manager.id,
            locationCode: manager.locationCode,
            name: manager.name,
            role: manager.roleTitle || baseRecord.role || 'Manager',
            position: manager.roleTitle || baseRecord.position || 'Manager',
            department: manager.locationName || manager.locationCode || baseRecord.department || 'Dashboard',
            managerDbId: manager.managerDbId,
            attendance: [],
            vacations: approvedVacationEntriesByManager[manager.managerDbId] || []
          }];
        })
        .filter(([, value]) => Boolean(value))
    )
  }), [approvedVacationEntriesByManager, managers]);

  const myRequests = useMemo(
    () => accessibleEvents
      .filter(event => isVacationEvent(event) && Number(event.managerId) === Number(user?.managerId))
      .sort((a, b) => `${b.startDate}${b.createdAt || ''}`.localeCompare(`${a.startDate}${a.createdAt || ''}`)),
    [accessibleEvents, user]
  );

  const scopedVacationRequests = useMemo(() => {
    const baseEvents = accessibleEvents.filter(isVacationEvent);

    if (isDirectorShellUser) {
      if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
        return baseEvents;
      }

      return baseEvents.filter(event => {
        if (selectedLocation?.id && Number(event.locationId) === Number(selectedLocation.id)) {
          return true;
        }

        return event.locationCode === selectedLocationCode;
      });
    }

    if (user?.locationCode) {
      return baseEvents.filter(event => event.locationCode === user.locationCode);
    }

    return baseEvents;
  }, [accessibleEvents, isDirectorShellUser, selectedLocation, selectedLocationCode, user]);

  const stationSummary = useMemo(
    () => buildStationSummary({
      dashboardData: visibleDashboardData,
      managers: isDirectorShellUser ? directorScopedManagers : stationManagers,
      locationCode: isDirectorShellUser && selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : undefined,
      locationLabel: isDirectorShellUser
        ? (selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode))
        : undefined,
      requests: scopedVacationRequests,
      attendanceSummary: attendanceScopeSummary,
      stationCountOverride: isDirectorShellUser
        ? (selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? directorLocationOptions.length : 1)
        : undefined,
      user
    }),
    [allDirectorLocationsLabel, attendanceScopeSummary, directorLocationOptions.length, directorScopedManagers, isDirectorShellUser, scopedVacationRequests, selectedLocation, selectedLocationCode, stationManagers, user, visibleDashboardData]
  );

  const directorLocationTotalRecord = useMemo(
    () => isDirectorShellUser
      ? buildDirectorLocationTotalRecord({
        dashboardData: visibleDashboardData,
        locationCode: selectedLocationCode,
        locationLabel: selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode),
        managers: directorScopedManagers,
        stationSummary
      })
      : null,
    [allDirectorLocationsLabel, directorScopedManagers, isDirectorShellUser, selectedLocation, selectedLocationCode, stationSummary, visibleDashboardData]
  );

  const directorGoalTargets = useMemo(
    () => isDirectorShellUser
      ? buildDirectorGoalTargets({
        goals: accessibleGoals,
        period: selectedPeriod,
        locationCode: selectedLocationCode
      })
      : {},
    [accessibleGoals, isDirectorShellUser, selectedLocationCode, selectedPeriod]
  );

  const selectedLocationGoalCount = useMemo(() => {
    if (!selectedLocationCode || selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
      return accessibleGoals.length;
    }
    return accessibleGoals.filter(goal => goal.locationCode === selectedLocationCode).length;
  }, [accessibleGoals, selectedLocationCode]);

  const selectedLocationManagerCount = useMemo(() => (
    directorScopedManagers.filter(manager => !isGlobalDirectorManager(manager)).length
  ), [directorScopedManagers]);

  const handleNavigate = (path, options = {}) => {
    const rawPath = String(path || defaultPath || '/');
    let targetPath = rawPath;
    let targetSearch = '';

    try {
      const nextUrl = new URL(rawPath, window.location.origin);
      targetPath = nextUrl.pathname || rawPath.split('?')[0] || '/';
      targetSearch = nextUrl.search || '';
    } catch {
      const [pathname, search = ''] = rawPath.split('?');
      targetPath = pathname || '/';
      targetSearch = search ? `?${search}` : '';
    }

    const explicitState = readUrlState(targetSearch);
    const hasExplicitFilterState = Boolean(
      explicitState[URL_KEY_LOCATION]
      || explicitState[URL_KEY_PERIOD]
      || explicitState[URL_KEY_MANAGER]
    );

    if (targetPath === currentPath && !hasExplicitFilterState) {
      return;
    }

    const { preserveFilters = true } = options;
    const nextUrl = preserveFilters && !hasExplicitFilterState
      ? buildPathWithState(targetPath, {
        [URL_KEY_LOCATION]: selectedLocationCode && selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
          ? selectedLocationCode
          : null,
        [URL_KEY_PERIOD]: selectedPeriod && selectedPeriod !== DEFAULT_SELECTED_PERIOD
          ? selectedPeriod
          : null
      })
      : rawPath;

    window.history.pushState({}, '', nextUrl);

    if (explicitState[URL_KEY_LOCATION]) {
      setSelectedLocationCode(explicitState[URL_KEY_LOCATION]);
    }
    if (explicitState[URL_KEY_PERIOD]) {
      setSelectedPeriod(explicitState[URL_KEY_PERIOD]);
    }
    if (explicitState[URL_KEY_MANAGER]) {
      setSelectedManager(explicitState[URL_KEY_MANAGER]);
    }

    setCurrentPath(targetPath);
  };

  const handleDirectorLocationChange = (nextLocationCode) => {
    setSelectedLocationCode(nextLocationCode);
    setSelectedManager(buildDirectorLocationTotalId(nextLocationCode));
  };

  const handleOpenPasswordPanel = async () => {
    if (user?.permissions?.includes('manage_users')) {
      try {
        const scopedQuery = isDirectorShellUser && selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
          ? `?locationCode=${encodeURIComponent(selectedLocationCode)}`
          : '';
        const userRows = await apiRequest(`/api/users${scopedQuery}`);
        setManagedUsers(userRows);
      } catch (error) {
        setScreenError(error.message || 'Unable to load user list for password resets.');
      }
    }

    setIsPasswordPanelOpen(true);
  };

  const periods = [
    { value: 'monthly', label: 'Monthly', icon: 'fa-calendar' },
    { value: 'yearly', label: 'Yearly', icon: 'fa-calendar-alt' }
  ];

  const headerSummary = useMemo(
    () => isStarterMode
      ? {
        label: 'Starter Mode',
        value: user?.companyName || PRODUCT_NAME,
        subtitle: 'Setup, preview, locations, and team'
      }
      : buildHeaderSummary({
      currentWorkspace: activeWorkspace,
      selectedLocationCode,
      selectedLocationLabel: selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode),
      stationSummary,
      user
    }),
    [activeWorkspace, allDirectorLocationsLabel, isStarterMode, selectedLocation, selectedLocationCode, stationSummary, user]
  );

  const directorManagerOptions = useMemo(
    () => isDirectorShellUser
      ? [{
        id: buildDirectorLocationTotalId(selectedLocationCode),
        name: selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? `${allDirectorLocationsLabel} Total` : `${selectedLocationCode} Total`
      }, ...directorSelectableManagers]
      : managers,
    [allDirectorLocationsLabel, directorSelectableManagers, isDirectorShellUser, managers, selectedLocationCode]
  );
  const roleScopedLocationCode = !isDirectorShellUser
    ? (stationSummary.locationCode || user?.locationCode || '')
    : '';
  const roleScopedLocationLabel = !isDirectorShellUser
    ? (stationSummary.locationName || user?.locationName || roleScopedLocationCode || 'Assigned location')
    : '';
  const roleScopedScorecardManagerId = roleScopedLocationCode
    ? buildDirectorLocationTotalId(roleScopedLocationCode)
    : '';
  const roleScorecardManagerOptions = useMemo(() => {
    if (isDirectorShellUser || currentPath !== '/location-scorecard' || !roleScopedLocationCode) {
      return directorManagerOptions;
    }

    return [{
      id: roleScopedScorecardManagerId,
      name: `${roleScopedLocationCode} Total`
    }];
  }, [currentPath, directorManagerOptions, isDirectorShellUser, roleScopedLocationCode, roleScopedScorecardManagerId]);
  const selectedDirectorManagerRecord = useMemo(() => {
    if (!selectedManager || String(selectedManager).startsWith(DIRECTOR_LOCATION_TOTAL_ID_PREFIX)) {
      return null;
    }

    return accessibleManagers.find(manager => (
      String(manager.id) === String(selectedManager)
      || String(manager.managerDbId) === String(selectedManager)
    )) || null;
  }, [accessibleManagers, selectedManager]);
  const selectedDirectorManagerDbId = selectedDirectorManagerRecord?.managerDbId
    ? String(selectedDirectorManagerRecord.managerDbId)
    : '';

  const handleSelectedManagerChange = (nextManagerValue) => {
    const normalizedValue = String(nextManagerValue || '');
    if (!normalizedValue) {
      setSelectedManager('');
      return;
    }

    if (normalizedValue.startsWith(DIRECTOR_LOCATION_TOTAL_ID_PREFIX)) {
      setSelectedManager(normalizedValue);
      return;
    }

    const resolvedManager = accessibleManagers.find(manager => (
      String(manager.id) === normalizedValue
      || String(manager.managerDbId) === normalizedValue
    ));

    setSelectedManager(resolvedManager?.id || normalizedValue);
  };

  const currentUserManager = useMemo(() => {
    if (user?.managerId) {
      return accessibleManagers.find(manager => Number(manager.managerDbId) === Number(user.managerId)) || null;
    }

    const normalizedEmail = String(user?.email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      return null;
    }

    return accessibleManagers.find(manager => String(manager.email || '').trim().toLowerCase() === normalizedEmail) || null;
  }, [accessibleManagers, user]);
  const currentUserRoleLabel = useMemo(
    () => formatTenantRoleLabel(user?.role),
    [formatTenantRoleLabel, user]
  );
  const currentUserDisplayName = useMemo(
    () => getUserDisplayName(user),
    [user]
  );
  const currentUserReferenceTitle = useMemo(
    () => getUserReferenceTitle(user),
    [user]
  );
  const currentWorkspaceLabel = useMemo(
    () => formatTenantWorkspaceLabel(activeWorkspace),
    [activeWorkspace, formatTenantWorkspaceLabel]
  );
  const currentScopeLabel = useMemo(
    () => isStarterMode
      ? 'Starter Mode'
      : buildScopeLabel({
      isDirectorShellUser,
      selectedLocation,
      selectedLocationCode,
      stationSummary,
      allDirectorLocationsLabel,
      user
    }),
    [allDirectorLocationsLabel, isDirectorShellUser, isStarterMode, selectedLocation, selectedLocationCode, stationSummary, user]
  );
  const currentAuthorityLabel = useMemo(() => {
    if (!user?.role) {
      return '';
    }

    if (activeWorkspace === 'admin') {
      return 'Platform Governance';
    }

    if (activeWorkspace === 'director') {
      if (
        selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
        && selectedLocationOwnerId
        && currentUserManager?.managerDbId
        && Number(selectedLocationOwnerId) === Number(currentUserManager.managerDbId)
      ) {
        return 'Primary Owner';
      }

      return 'Director Scope';
    }

    if (activeWorkspace === 'airport') {
      return 'Location Operations';
    }

    if (activeWorkspace === 'ops') {
      return 'Assigned Manager Scope';
    }

    return currentUserRoleLabel;
  }, [activeWorkspace, currentUserManager, currentUserRoleLabel, selectedLocationCode, selectedLocationOwnerId, user]);

  const directorDashboardData = useMemo(() => {
    if (!isDirectorShellUser) {
      return visibleDashboardData;
    }

    return {
      ...visibleDashboardData,
      managers: {
        ...Object.fromEntries(
          directorScopedManagers
            .map(manager => [manager.id, visibleDashboardData.managers[manager.id]])
            .filter(([, value]) => Boolean(value))
        ),
        [directorLocationTotalRecord.id]: directorLocationTotalRecord
      }
    };
  }, [directorLocationTotalRecord, directorScopedManagers, isDirectorShellUser, visibleDashboardData]);

  const directorDashboardDataWithLiveGoals = useMemo(() => {
    if (!isDirectorShellUser || !directorLocationTotalRecord) {
      return directorDashboardData;
    }

    return {
      ...directorDashboardData,
      managers: {
        ...directorDashboardData.managers,
        [directorLocationTotalRecord.id]: {
          ...directorLocationTotalRecord,
          goalMap: {
            ...directorLocationTotalRecord.goalMap,
            ...directorGoalTargets
          }
        }
      }
    };
  }, [directorDashboardData, directorGoalTargets, directorLocationTotalRecord, isDirectorShellUser]);
  const roleScopedLocationTotalRecord = useMemo(() => {
    if (isDirectorShellUser || !roleScopedLocationCode) {
      return null;
    }

    return buildDirectorLocationTotalRecord({
      dashboardData: visibleDashboardData,
      locationCode: roleScopedLocationCode,
      locationLabel: roleScopedLocationLabel,
      managers: stationManagers,
      stationSummary
    });
  }, [isDirectorShellUser, roleScopedLocationCode, roleScopedLocationLabel, stationManagers, stationSummary, visibleDashboardData]);
  const roleDashboardDataWithLocationTotal = useMemo(() => {
    if (!roleScopedLocationTotalRecord) {
      return visibleDashboardData;
    }

    return {
      ...visibleDashboardData,
      managers: {
        ...visibleDashboardData.managers,
        [roleScopedLocationTotalRecord.id]: roleScopedLocationTotalRecord
      }
    };
  }, [roleScopedLocationTotalRecord, visibleDashboardData]);

  const directorScopedPolicies = useMemo(() => {
    if (!isDirectorShellUser) {
      return policies;
    }

    if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
      return policies;
    }

    return policies.filter(policy => policy.locationCode === selectedLocationCode);
  }, [isDirectorShellUser, policies, selectedLocationCode]);

  const attendanceSummaryUrl = useMemo(() => {
    if (!isAuthenticated || !user) {
      return null;
    }
    const { startDate, endDate } = deriveDateRangeForPeriod(selectedPeriod);
    let scopedLocationCode = null;
    if (isDirectorShellUser) {
      scopedLocationCode = selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : null;
    } else if (user.locationCode) {
      scopedLocationCode = user.locationCode;
    }
    const qs = buildQuery({ startDate, endDate, locationCode: scopedLocationCode });
    return `/api/events/attendance/summary?${qs}`;
  }, [isAuthenticated, isDirectorShellUser, selectedLocationCode, selectedPeriod, user]);

  const { data: attendanceSummaryPayload, error: attendanceSummaryError } = useFetch(
    attendanceSummaryUrl,
    { deps: [accessibleEvents] }
  );

  useEffect(() => {
    if (!attendanceSummaryUrl) {
      setAttendanceScopeSummary(null);
      return;
    }
    if (attendanceSummaryError) {
      setAttendanceScopeSummary(null);
      return;
    }
    if (attendanceSummaryPayload !== undefined) {
      setAttendanceScopeSummary(attendanceSummaryPayload);
    }
  }, [attendanceSummaryUrl, attendanceSummaryPayload, attendanceSummaryError]);

  const showDashboardControls = useMemo(() => (
    !isStarterMode && (
    resolvedCurrentPath === '/my-dashboard'
    || resolvedCurrentPath === '/station-dashboard'
    || resolvedCurrentPath === '/ops-managers'
    || resolvedCurrentPath === '/director-dashboard'
    || resolvedCurrentPath === '/location-scorecard'
    || resolvedCurrentPath === '/requests-calendar'
    )
  ), [resolvedCurrentPath, isStarterMode]);
  const isStarterSetupRoute = isStarterMode && resolvedCurrentPath === '/setup';
  const isStarterPreviewRoute = isStarterMode && resolvedCurrentPath === '/dashboard-preview';
  const isStarterDirectoryRoute = isStarterMode && (resolvedCurrentPath === '/locations' || resolvedCurrentPath === '/managers');
  const isLocationSetupRoute = resolvedCurrentPath === '/location-setup' && isDirectorShellUser;
  // W1: legacy (/employees*) and canonical (/workforce*) paths both
  // resolve through resolveWorkforceRoute, so the boolean flags below
  // are TRUE for either URL family. Downstream rendering is unchanged.
  const workforceRoute = resolveWorkforceRoute(resolvedCurrentPath);
  const isPeopleTalentRoute = Boolean(workforceRoute);
  const isLocationStarterActionRoute = ['/goals', '/actuals', '/managers', '/locations'].includes(resolvedCurrentPath)
    || isPeopleTalentRoute;
  const showLocationStarterMode = Boolean(
    isDirectorShellUser
    && !isStarterMode
    && !isLocationSetupRoute
    && !isLocationStarterActionRoute
    && selectedLocationCode
    && selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
    && selectedLocationGoalCount === 0
    && selectedLocationManagerCount === 0
  );

  // Centralized route resolution (Milestone 1).
  // The boolean flags above stay in place for back-compat with the
  // showLocationStarterMode computation and the legacy role-shell
  // fall-through renders below — but every conditional below now reads
  // routeEntry.id instead of hand-coded path strings.
  const routeEntry = useMemo(
    () => resolveRouteEntry(resolvedCurrentPath, user, {
      isStarterMode,
      isLocationSetupActive: isLocationSetupRoute,
      showLocationStarterMode
    }),
    [resolvedCurrentPath, user, isStarterMode, isLocationSetupRoute, showLocationStarterMode]
  );
  const routeId = routeEntry?.id || null;
  const currentRouteModuleId = useMemo(
    () => resolveRouteModuleId(resolvedCurrentPath),
    [resolvedCurrentPath]
  );
  const currentRouteModuleEnabled = isModuleEnabled(currentRouteModuleId);
  const currentRouteModuleName = getModuleMetadata(currentRouteModuleId)?.name || 'This module';
  const opsContextBarLocations = useMemo(() => (
    (accessibleLocations || [])
      .filter(loc => loc && loc.isActive !== false)
      .map(loc => ({ code: loc.code, name: loc.name || loc.code }))
  ), [accessibleLocations]);

  if (isLoading) {
    return (
      <div className="app-loading-screen">
        <div className="app-loading-card">
          <h1>Loading secure dashboard</h1>
          <p>Checking your session before any KPI data is shown.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <LoginPage
        errorMessage=""
        onLogin={login}
        systemError={authError}
      />
    );
  }

  const shouldShowOpsContextBar = resolveOpsContextBarFromEntry(routeEntry, {
    isStarterMode,
    isLocationSetupActive: isLocationSetupRoute,
    showLocationStarterMode
  });

  return (
    <div className="App">
      <OperationalContextProvider>
      <ProtectedRoute>
        <Navigation
          activeAuthorityLabel={currentAuthorityLabel}
          activeScopeLabel={currentScopeLabel}
          badgeCounts={{
            '/today': pendingCounts.focusQueueTotal > 0 ? pendingCounts.focusQueueTotal : 0,
            '/requests-calendar': pendingCounts.returnedToMe > 0 ? pendingCounts.returnedToMe : 0,
            '/director-dashboard': pendingCounts.pendingForReview > 0 ? pendingCounts.pendingForReview : 0
          }}
          currentPath={resolvedCurrentPath}
          navGroups={navGroups}
          navItems={navItems}
          onExitClientPortal={user?.viewingClientPortal ? handleExitClientPortal : null}
          onLogout={logout}
          onNavigate={handleNavigate}
          onOpenPasswordPanel={handleOpenPasswordPanel}
          onRefreshCurrentUser={refreshCurrentUser}
          runtimeVersion={runtimeVersion}
          user={user}
        />
        {isDiagnosticOpen ? (
          <SessionDiagnosticPanel onClose={() => setIsDiagnosticOpen(false)} />
        ) : null}
        {user?.viewingClientPortal && (
          <div className="client-portal-banner" role="status">
            <div>
              <strong>Viewing client portal as VizionOps Admin</strong>
              <span>{user.companyName || user.clientCompanyName || 'Client Company'}</span>
            </div>
            <button type="button" onClick={handleExitClientPortal}>
              Exit to VizionOps
            </button>
          </div>
        )}
        {isPasswordPanelOpen && (
          <PasswordPanel
            canManageUsers={Boolean(user?.permissions?.includes('manage_users'))}
            onChangePassword={changePassword}
            onClose={() => setIsPasswordPanelOpen(false)}
            onResetPassword={resetUserPassword}
            user={user}
            users={managedUsers}
          />
        )}
        <div className="app-container">
          {shouldShowOpsContextBar ? (
            <OperationalContextBar
              locations={opsContextBarLocations}
              hideKpi
              data-testid="app-operational-context-bar"
              onLocationChange={(nextCode) => {
                // Small wiring bridge: mirror bar-driven location into
                // App's own selectedLocationCode so scorecard / goals /
                // executive pipelines (which still read App state)
                // stay in sync with the compact context UI.
                if (!isDirectorShellUser) return;
                const normalized = nextCode || ALL_DIRECTOR_LOCATIONS;
                if (normalized !== selectedLocationCode) {
                  handleDirectorLocationChange(normalized);
                }
              }}
            />
          ) : null}
          {appToast.message && (
            <div className={`app-toast app-toast-${appToast.type}`} role="status" aria-live="polite">
              <i className={`fas ${
                appToast.type === 'error'
                  ? 'fa-circle-exclamation'
                  : appToast.type === 'warning'
                    ? 'fa-triangle-exclamation'
                    : 'fa-check-circle'
              }`}></i>
              <span>{appToast.message}</span>
              {appToast.action?.label && (
                <button className="app-toast-action" type="button" onClick={handleToastAction}>
                  {appToast.action.label}
                </button>
              )}
            </div>
          )}
          <div className="controls-panel">
          <div className="control-summary">
            <span className="summary-label">{headerSummary.label}</span>
            <span className="summary-value">{headerSummary.value}</span>
            <span className="summary-subtitle">{headerSummary.subtitle}</span>
          </div>

          {(showDashboardControls || showDirectorLocationControl) && (
            <>
              {/*
                When the compact OperationalContextBar is mounted, its
                Location dropdown already represents director-level
                "location scope". Hiding the duplicate Location Scope
                <select> here removes the visually-redundant control
                the user flagged on Executive Snapshot — manager
                selection on non-director shells is unaffected.
              */}
              {(!isDirectorShellUser || showDirectorLocationControl)
                && !(shouldShowOpsContextBar && isDirectorShellUser) && (
                <div className="control-group" data-testid="app-location-scope-control">
                  <label htmlFor={isDirectorShellUser ? 'location-select' : 'manager-select'}>
                    <i className={`fas ${isDirectorShellUser ? 'fa-building' : 'fa-user-circle'}`}></i> {isDirectorShellUser ? 'Location Scope' : 'Select Manager'}
                  </label>
                  {isDirectorShellUser ? (
                    <select
                      id="location-select"
                      value={selectedLocationCode}
                      onChange={(e) => handleDirectorLocationChange(e.target.value)}
                      className="select-input"
                    >
                      {showRollupOption ? (
                        <option value={ALL_DIRECTOR_LOCATIONS}>{allDirectorLocationsLabel}</option>
                      ) : null}
                      {directorVisibleLocationOptions.map(location => (
                        <option key={location.code} value={location.code}>{location.code} - {location.name}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      id="manager-select"
                      value={selectedManager}
                      onChange={(e) => setSelectedManager(e.target.value)}
                      className="select-input"
                      disabled={activeWorkspace === 'ops' || roleScorecardManagerOptions.length <= 1 || resolvedCurrentPath === '/location-scorecard'}
                    >
                      {roleScorecardManagerOptions.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {isDirectorShellUser && resolvedCurrentPath === '/location-scorecard' && (
                <div className="control-group">
                  <label htmlFor="manager-select">
                    <i className="fas fa-chart-pie"></i> Scorecard View
                  </label>
                  <select
                    id="manager-select"
                    value={selectedManager}
                    onChange={(e) => setSelectedManager(e.target.value)}
                    className="select-input"
                    disabled={directorManagerOptions.length <= 1}
                  >
                    {directorManagerOptions.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {resolvedCurrentPath === '/location-scorecard' && (
                <>
                  <div className="control-group">
                    <label htmlFor="period-select">
                      <i className="fas fa-clock"></i> Time Period
                    </label>
                    <select
                      id="period-select"
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                      className="select-input"
                    >
                      {periods.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>

                  <button className="print-btn no-print" onClick={() => window.print()}>
                    <i className="fas fa-print"></i> Print Scorecard
                  </button>
                </>
              )}
            </>
          )}
          </div>

          {screenError && <div className="auth-notice error">{screenError}</div>}

          {!currentRouteModuleEnabled && (
            <ModuleNotEnabledPage
              moduleName={currentRouteModuleName}
              onGoHome={() => handleNavigate(defaultPath)}
            />
          )}

          {currentRouteModuleEnabled && isStarterSetupRoute && (
            <SetupWizard
              onboardingStatus={onboardingStatus}
              onNavigate={handleNavigate}
              onStatusChange={setOnboardingStatus}
              user={user}
              accessibleLocations={accessibleLocations}
            />
          )}

          {currentRouteModuleEnabled && isStarterPreviewRoute && (
            <DashboardPreview
              onboardingStatus={onboardingStatus}
              onNavigate={handleNavigate}
              user={user}
            />
          )}

          {currentRouteModuleEnabled && isLocationSetupRoute && (
            <LocationSetupPage
              onNavigate={handleNavigate}
              user={user}
            />
          )}

          {currentRouteModuleEnabled && showLocationStarterMode && (
            <StarterMode
              locationLabel={selectedLocation?.name ? `${selectedLocationCode} - ${selectedLocation.name}` : selectedLocationCode}
              onNavigate={handleNavigate}
            />
          )}

          {currentRouteModuleEnabled && routeId === ROUTE_IDS.today && (
              <DashboardErrorBoundary
                resetKey="today"
                title="Today hit an unexpected error."
                message="Refresh the page or switch sections to reload your focus queue."
              >
                <TodayPage onNavigate={handleNavigate} />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && activeWorkspace === 'ops' && !isStarterMode && !isLocationSetupRoute && !showLocationStarterMode && routeId !== ROUTE_IDS.today && routeId !== ROUTE_IDS.attendance && routeId !== ROUTE_IDS['manager-timecards'] && routeId !== ROUTE_IDS['timekeeping-reports'] && (
              <DashboardErrorBoundary
                resetKey={`ops:${selectedManager}:${selectedPeriod}:${resolvedCurrentPath}`}
                title={`${formatTenantRoleLabel('ops_manager')} dashboard hit an unexpected error.`}
                message="Refresh the page or switch sections to reload your dashboard."
              >
                <OpsManagerDashboard
                  authorityLabel={currentAuthorityLabel}
                  currentPath={resolvedCurrentPath}
                  dashboardData={roleDashboardDataWithLocationTotal}
                  myRequests={myRequests}
                  onNavigate={handleNavigate}
                period={selectedPeriod}
                selectedManager={selectedManager}
                scopeLabel={currentScopeLabel}
                stationSummary={stationSummary}
                userDisplayName={currentUserDisplayName}
                userReferenceTitle={currentUserReferenceTitle}
                user={user}
                workspaceLabel={currentWorkspaceLabel}
              />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && activeWorkspace === 'airport' && !isStarterMode && !isLocationSetupRoute && !showLocationStarterMode && routeId !== ROUTE_IDS.today && routeId !== ROUTE_IDS.attendance && routeId !== ROUTE_IDS['manager-timecards'] && routeId !== ROUTE_IDS['timekeeping-reports'] && (
              <DashboardErrorBoundary
                resetKey={`airport:${selectedManager}:${selectedPeriod}:${resolvedCurrentPath}`}
                title={`${formatTenantRoleLabel('airport_manager')} dashboard hit an unexpected error.`}
                message="Refresh the page or switch sections to reload your station view."
              >
                <AirportManagerDashboard
                  authorityLabel={currentAuthorityLabel}
                  currentPath={resolvedCurrentPath}
                  dashboardData={roleDashboardDataWithLocationTotal}
                  onManagerChange={handleSelectedManagerChange}
                  onNavigate={handleNavigate}
                  period={selectedPeriod}
                  selectedManager={selectedManager}
                  scopeLabel={currentScopeLabel}
                  stationPolicies={directorScopedPolicies.filter(policy => policy.locationCode === stationSummary.locationCode)}
                  stationRequests={scopedVacationRequests}
                  stationManagers={stationManagers}
                  stationSummary={stationSummary}
                  userDisplayName={currentUserDisplayName}
                  userReferenceTitle={currentUserReferenceTitle}
                  user={user}
                  workspaceLabel={currentWorkspaceLabel}
                />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && routeId === ROUTE_IDS.executive && (
              <DashboardErrorBoundary
                resetKey={`executive:${selectedLocationCode}:${selectedPeriod}`}
                title="Executive Snapshot hit an unexpected error."
                message="Refresh the page or switch sections to reload your leadership view."
              >
                <ExecutiveDashboard
                  user={user}
                  selectedLocationCode={selectedLocationCode}
                  selectedPeriod={selectedPeriod}
                  locationLabel={selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode)}
                  onNavigate={handleNavigate}
                />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && routeId === ROUTE_IDS.alerts && (
              <DashboardErrorBoundary
                resetKey={`alerts:${selectedLocationCode}:${selectedPeriod}`}
                title="Alerts Center hit an unexpected error."
                message="Refresh the page or switch sections to reload the alerts view."
              >
                <AlertsCenter
                  selectedLocationCode={selectedLocationCode}
                  selectedPeriod={selectedPeriod}
                  locationLabel={selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode)}
                  onNavigate={handleNavigate}
                />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && routeId === ROUTE_IDS.notifications && (
              <DashboardErrorBoundary
                resetKey="notifications"
                title="Notifications inbox hit an unexpected error."
                message="Refresh the page to reload your notifications."
              >
                <NotificationsInbox onNavigate={handleNavigate} />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && routeId === ROUTE_IDS['goal-plan'] && (
              <DashboardErrorBoundary
                resetKey={`goal-plan:${selectedLocationCode}:${selectedPeriod}`}
                title="Goal Plan hit an unexpected error."
                message="Refresh the page or switch sections to reload goal planning."
              >
                <GoalPlanPage
                  selectedLocationCode={selectedLocationCode}
                  selectedPeriod={selectedPeriod}
                  locationLabel={selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode)}
                />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && routeId === ROUTE_IDS['manager-timecards'] && (
            <DashboardErrorBoundary
              resetKey="manager-timecards"
              title="Timecards hit an unexpected error."
              message="Refresh the page to reload authorized employee time entries."
            >
              <ManagerTimecardsPage user={user} accessibleLocations={accessibleLocations} />
            </DashboardErrorBoundary>
          )}
          {currentRouteModuleEnabled && routeId === ROUTE_IDS.attendance && (
            <AttendancePage accessibleLocations={accessibleLocations} />
          )}
          {currentRouteModuleEnabled && routeId === ROUTE_IDS['timekeeping-reports'] && (
            <DashboardErrorBoundary resetKey="timekeeping-reports" title="Recorded Hours hit an unexpected error." message="Refresh the page to reload the report.">
              <TimekeepingReportsPage accessibleLocations={accessibleLocations} user={user} />
            </DashboardErrorBoundary>
          )}
          {currentRouteModuleEnabled && routeId === ROUTE_IDS['timekeeping-admin'] && (
            <DashboardErrorBoundary resetKey="timekeeping-admin" title="Timekeeping Administration hit an unexpected error." message="Refresh the page to reload authorized setup information.">
              <TimekeepingAdminPage user={user} />
            </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && (routeId === ROUTE_IDS['people-talent-profile-by-profile']
            || routeId === ROUTE_IDS['workforce-canonical-employee']
            || routeId === ROUTE_IDS['people-talent-profile']) && (
              <DashboardErrorBoundary
                resetKey={`employee-profile:${resolvedCurrentPath}`}
                title="Person Workspace hit an unexpected error."
                message="Refresh the page or switch sections to reload this employee profile."
              >
                <EmployeeProfilePage
                  managerIdOverride={
                    personWorkspaceIdentity.personIdentityType === 'manager'
                      ? personWorkspaceIdentity.managerId
                      : null
                  }
                  onNavigate={handleNavigate}
                  profileIdOverride={
                    personWorkspaceIdentity.personIdentityType === 'employee_profile'
                      ? personWorkspaceIdentity.employeeProfileId
                      : null
                  }
                />
              </DashboardErrorBoundary>
          )}

          {currentRouteModuleEnabled && routeEntry?.workspaceGroup === 'people-talent'
            && routeId !== ROUTE_IDS['people-talent-profile-by-profile']
            && routeId !== ROUTE_IDS['workforce-canonical-employee']
            && routeId !== ROUTE_IDS['people-talent-profile'] && (
              <DashboardErrorBoundary
                resetKey={`employees:${resolvedCurrentPath}:${personWorkspaceIdentity.managerId || ''}:${personWorkspaceIdentity.employeeProfileId || ''}`}
                title="People & Talent hit an unexpected error."
                message="Refresh the page or switch sections to reload employee profiles and documents."
              >
                <EmployeeDirectoryPage
                  user={user}
                  defaultExpiringExpanded={workforceRoute?.focus === 'expiring'}
                  onNavigate={handleNavigate}
                  selectedManagerIdExternal={
                    Number.isFinite(workforceRoute?.managerId)
                      ? workforceRoute.managerId
                      : null
                  }
                  workspaceFocus={
                    // EmployeeDirectoryPage's FOCUS_COPY keys on legacy
                    // paths today; W1 keeps that contract by passing the
                    // legacy path equivalent regardless of which URL
                    // family the operator arrived from. Telemetry +
                    // copy semantics stay byte-identical.
                    workforceRoute
                      ? legacyPathForFocus(workforceRoute.focus)
                      : resolvedCurrentPath
                  }
                  useCanonicalUrls={Boolean(workforceRoute?.isCanonical)}
                />
              </DashboardErrorBoundary>
          )}

          {/* AdminDashboard fallback — renders any admin-shell path NOT
              already claimed by a declarative route table entry. */}
          {currentRouteModuleEnabled && isAdminUser && !routeEntry && !isStarterMode && !isLocationSetupRoute && !showLocationStarterMode && (
              <AdminDashboard
                currentPath={resolvedCurrentPath}
                locationLabel={selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode)}
                onNavigate={handleNavigate}
                selectedLocationCode={selectedLocationCode}
                onEnterClientPortal={handleEnterClientPortal}
                user={user}
                userDisplayName={currentUserDisplayName}
                userReferenceTitle={currentUserReferenceTitle}
                workspaceLabel={currentWorkspaceLabel}
              />
          )}

          {/* DirectorDashboard fallback — same pattern. Renders any
              director-shell path NOT claimed by the route table. */}
          {currentRouteModuleEnabled && ((activeWorkspace === 'director' && !isStarterMode) || isStarterDirectoryRoute) && !routeEntry && !isLocationSetupRoute && !showLocationStarterMode && (
              <DirectorDashboard
                allManagers={directorScopedManagers}
                authorityLabel={currentAuthorityLabel}
                currentUserManager={currentUserManager}
                currentPath={resolvedCurrentPath}
                dashboardData={directorDashboardDataWithLiveGoals}
                locationLabel={selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? allDirectorLocationsLabel : (selectedLocation?.name || selectedLocationCode)}
                locationDirectory={accessibleLocations}
                managerOptions={directorManagerOptions}
                locationOwnerLabel={selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? '' : selectedLocationOwnerLabel}
                onManagerChange={handleSelectedManagerChange}
                onNavigate={handleNavigate}
                period={selectedPeriod}
                policies={directorScopedPolicies}
                requests={scopedVacationRequests}
                scopeLabel={currentScopeLabel}
                selectedManager={selectedManager}
                selectedManagerDbId={selectedDirectorManagerDbId}
                selectedLocationCode={selectedLocationCode}
                stationSummary={stationSummary}
                userDisplayName={currentUserDisplayName}
                userReferenceTitle={currentUserReferenceTitle}
                user={user}
                workspaceLabel={currentWorkspaceLabel}
              />
          )}

          {currentRouteModuleEnabled && !isKnownWorkspaceUser(user) && (
            <UnauthorizedPage onGoHome={() => handleNavigate(defaultPath)} />
          )}
        </div>
      </ProtectedRoute>
      </OperationalContextProvider>
    </div>
  );
}

export default App;
